<template>
  <layout>
    <div class="zone-wrapper">
      <input
        id="pac-input"
        class="controls"
        type="text"
        placeholder="Type to search"
      />
      <!--   map will be injected here   -->
      <div id="map-holder" class="h-100"></div>
      <manager-panel
        :show="zoneManagerPanelVisibility"
        @on-country-zone-row-click="onCountryZoneRowClick"
        @on-in-country-zone-row-click="onCountryZoneRowClick"
        @on-tab-changed="onTabChanged"
      ></manager-panel>
    </div>
  </layout>
</template>

<script>
/* global google */
import ManagerPanel from './zone-manager-panel'
import EventBus from '../event-bus'
import Swal from 'sweetalert2'
import Mixins from '../mixins'

import { mapActions, mapGetters } from 'vuex'
import { loadGoogleMaps } from 'stylemix-base'
import { divisionPopup } from './popups'

const TAB_COUNTRY_ZONE = 0
const TAB_IN_COUNTRY_ZONE = 1

export default {
  name: 'ZoneEditorView',

  components: {
    ManagerPanel,
  },

  mixins: [Mixins],

  data() {
    return {
      map: null,
      tab: 0,
    }
  },

  computed: mapGetters('zones', [
    'zoneManagerPanelVisibility',
    'activeCountryZone',
    'activeInCountryZone',
  ]),

  created() {
    EventBus.$on('on-zone-removed', this.clearMapDataLayer)
  },

  beforeDestroy() {
    EventBus.$off('on-zone-removed', this.clearMapDataLayer)
  },

  mounted() {
    loadGoogleMaps(['places']).then(() => this.renderMap())
  },

  methods: {
    ...mapActions('zones', [
      'addCountryZone',
      'addInCountryZone',
      'updateCountryZones',
      'updateInCountryZones',
      'toggleZoneManagerPanelVisibility',
      'setActiveCountryZone',
      'unsetActiveCountryZone',
      'setActiveInCountryZone',
      'unsetActiveInCountryZone',
      'fetchInCountryZones',
    ]),

    initAutoComplete(map) {
      // Create the search box and link it to the UI element.
      const input = document.getElementById('pac-input')
      const searchBox = new google.maps.places.SearchBox(input)
      map.controls[google.maps.ControlPosition.TOP_LEFT].push(input)

      // Bias the SearchBox results towards current map's viewport.
      map.addListener('bounds_changed', function() {
        searchBox.setBounds(map.getBounds())
      })

      let markers = []
      // Listen for the event fired when the user selects a prediction and retrieve
      // more details for that place.
      searchBox.addListener('places_changed', function() {
        const places = searchBox.getPlaces()

        if (places.length == 0) {
          return
        }

        // Clear out the old markers.
        markers.forEach(function(marker) {
          marker.setMap(null)
        })
        markers = []

        // For each place, get the icon, name and location.
        const bounds = new google.maps.LatLngBounds()
        places.forEach(function(place) {
          if (!place.geometry) {
            /* eslint-disable-next-line no-console */
            console.log('Returned place contains no geometry')
            return
          }
          const icon = {
            url: place.icon,
            size: new google.maps.Size(71, 71),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(17, 34),
            scaledSize: new google.maps.Size(25, 25),
          }

          // Create a marker for each place.
          markers.push(
            new google.maps.Marker({
              map: map,
              icon: icon,
              title: place.name,
              position: place.geometry.location,
            }),
          )

          if (place.geometry.viewport) {
            // Only geocodes have viewport.
            bounds.union(place.geometry.viewport)
          } else {
            bounds.extend(place.geometry.location)
          }
        })
        map.fitBounds(bounds)
      })
    },

    renderMap(retryCount = 0) {
      if (typeof window.google === 'undefined') {
        if (retryCount < 20) {
          setTimeout(() => this.renderMap(retryCount + 1), 100)
        }
        return
      }
      const map = new window.google.maps.Map(document.getElementById('map-holder'), {
        center: { lat: 50.8503, lng: 4.3517 },
        zoom: 8,
      })

      map.data.setControls(['Polygon'])
      map.data.setStyle({
        editable: true,
        draggable: true,
        strokeWeight: 1,
        fillColor: 'red',
      })

      this.map = map

      this.addTabsControlButtonToMap()
      this.addSaveButton()
      this.addClearButton()
      this.initAutoComplete(map)
    },

    createMapButton(innerHtml, clickHandler, hint) {
      // Create a div to hold the control.
      const controlDiv = document.createElement('div')
      controlDiv.addEventListener('click', clickHandler)

      // Set CSS for the control border
      const controlUI = document.createElement('div')
      controlUI.style.backgroundColor = '#fff'
      controlUI.style.boxShadow = 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px'
      controlUI.style.borderRadius = '2px'
      controlUI.style.marginRight = '10px'
      controlUI.style.border = '1px solid #fff'
      controlUI.style.cursor = 'pointer'
      controlUI.style.marginBottom = '10px'
      controlUI.style.textAlign = 'center'
      controlUI.title = hint
      controlDiv.appendChild(controlUI)

      // Set CSS for the control interior
      const controlText = document.createElement('div')
      controlText.style.color = 'rgb(25,25,25)'
      controlText.style.fontFamily = 'Roboto,Arial,sans-serif'
      controlText.style.fontSize = '16px'
      const hw = '38px'
      controlText.style.height = hw
      controlText.style.width = hw
      controlText.style.lineHeight = hw
      controlText.style.fill = '#666666'
      controlText.style.color = '#666666'
      controlText.innerHTML = innerHtml
      controlUI.appendChild(controlText)

      return controlDiv
    },

    addTabsControlButtonToMap() {
      const toggleMapManagerButton = this.createMapButton(
        '<i class="icon-list"></i>',
        this.toggleZoneManagerPanelVisibility,
        this.$t('zones.toggle_zone_manager'),
      )
      this.map.controls[google.maps.ControlPosition.RIGHT_TOP].push(
        toggleMapManagerButton,
      )
    },

    addSaveButton() {
      const saveDataLayerButton = this.createMapButton(
        '<i class="icon-floppy-disk"></i>',
        this.saveCurrentDataLayer,
        this.$t('zones.save'),
      )
      this.map.controls[google.maps.ControlPosition.RIGHT_TOP].push(
        saveDataLayerButton,
      )
    },

    addClearButton() {
      const clearButton = this.createMapButton(
        '<i class="icon-cross2"></i>',
        this.onClearMapButtonClick,
        this.$t('zones.clear_map_layers'),
      )
      this.map.controls[google.maps.ControlPosition.RIGHT_TOP].push(clearButton)
    },

    onClearMapButtonClick() {
      this.clearMapDataLayer()
      if (this.tab === TAB_COUNTRY_ZONE) this.unsetActiveCountryZone()
      else this.unsetActiveInCountryZone()
    },

    async saveZone(geoJson) {
      const needUpdateInCountryZone =
        this.activeInCountryZone && this.tab === TAB_IN_COUNTRY_ZONE
      const needUpdateCountryZone =
        this.activeCountryZone && this.tab === TAB_COUNTRY_ZONE

      if (needUpdateInCountryZone || needUpdateCountryZone) {
        const zone = needUpdateCountryZone
          ? this.activeCountryZone
          : this.activeInCountryZone

        this.updateExistingZone({
          ...zone,
          geoJson,
        })
      } else {
        // add new zone
        if (this.tab === TAB_COUNTRY_ZONE) {
          const result = await divisionPopup(
            { title: '', isNewMethod: false },
            this.$i18n,
          )

          if (result.dismiss) {
            return
          }

          this.addCountryZone({
            title: result.value.name,
            isNewMethod: result.value.isNewMethod,
            geoJson,
          }).catch(err => this.$toast.error(err.message))
        } else {
          const { value: title } = await this.prompt(this.$t('zones.new_zone'))
          if (title) {
            const zoneForSave = {
              title,
              geoJson,
            }
            this.addInCountryZone({
              ...zoneForSave,
              division_id: this.activeCountryZone.id,
            }).catch(err => this.$toast.error(err.data.message))
          }
        }
      }
    },

    saveCurrentDataLayer() {
      this.map.data.toGeoJson(async geoJson => {
        if (!(geoJson.features && geoJson.features.length)) return

        await this.saveZone(geoJson)
      })
    },

    onCountryZoneRowClick(countryZone) {
      // clear old data layer
      this.clearMapDataLayer()

      // draw new data layer
      this.drawZoneOnMap(countryZone)
      this.fitMapToCurrentDataLayer()

      if (this.tab === TAB_COUNTRY_ZONE) {
        this.setActiveCountryZone(countryZone)
      } else if (this.tab === TAB_IN_COUNTRY_ZONE) {
        this.setActiveInCountryZone(countryZone)
      }
    },

    drawZoneOnMap(countryZone) {
      try {
        this.map.data.addGeoJson(countryZone.geoJson)
      } catch (err) {
        this.$toast.error(err.message)
      }
    },

    /**
     * clears all user drawing shapes/layers
     * from map's surface
     */
    clearMapDataLayer() {
      const data = this.map.data
      data.forEach(feature => data.remove(feature))
    },

    async updateExistingZone(zoneForUpdate) {
      const { value: yes } = await Swal({
        type: 'question',
        title: `${this.$t('zones.want_to_update_zone')} ${
          zoneForUpdate.title
        }?`,
        showCancelButton: true,
      })

      if (yes) {
        if (this.tab === TAB_COUNTRY_ZONE)
          this.updateCountryZones(zoneForUpdate).catch(err =>
            this.$toast.error(err.data.message),
          )
        else if (this.tab === TAB_IN_COUNTRY_ZONE)
          this.updateInCountryZones({
            ...zoneForUpdate,
            division_id: this.activeCountryZone.id,
          }).catch(err => this.$toast.error(err.data.message))
      }
    },

    fitMapToCurrentDataLayer() {
      const bounds = new google.maps.LatLngBounds()
      this.map.data.forEach(function(feature) {
        feature.getGeometry().forEachLatLng(function(latlng) {
          bounds.extend(latlng)
        })
      })

      this.map.fitBounds(bounds)
    },

    onTabChanged(tabIndex) {
      this.tab = tabIndex
      switch (tabIndex) {
        case TAB_COUNTRY_ZONE:
          this.unsetActiveInCountryZone()
          if (this.activeCountryZone)
            this.onCountryZoneRowClick(this.activeCountryZone)
          break
        case TAB_IN_COUNTRY_ZONE:
          this.clearMapDataLayer()
          this.fetchInCountryZones(this.activeCountryZone.id).catch(err =>
            this.$toast.error(err.data.message),
          )
          break
      }
    },
  },
}
</script>

<style scoped>
.zone-wrapper {
  display: flex;
  flex-direction: row;
  flex: 1;
}

.zone-wrapper #map-holder {
  flex: 2;
}
</style>
